import type {
  ChartType,
  FontSpec,
  TooltipCallbacks,
  TooltipOptions,
} from 'chart.js';

/**
 * chart.js' `Chart.defaults.font` values, copied literally.
 *
 * This module used to value-import `Chart` to read them, which dragged all of
 * chart.js (200 KB) onto the eager startup path of every app through this
 * framework-independent domain library — see
 * docs/performance/meta-app-bundle-size-audit-2026-09.md §5 #3.
 *
 * `tooltips.options.spec.ts` pins these literals to the installed chart.js, so
 * a defaults change upstream fails the test rather than drifting silently.
 */
const CHART_JS_DEFAULT_FONT_STYLE = 'normal';
const CHART_JS_DEFAULT_FONT_LINE_HEIGHT = 1.2;
const CHART_JS_DEFAULT_FONT_WEIGHT: FontSpec['weight'] = null;

/**
 * Note: this object no longer spreads `Chart.defaults.plugins.tooltip`. That
 * spread was always a no-op — plugin defaults only exist after
 * `Chart.register(Tooltip)`, which runs when `bd-chart-canvas`
 * (libs/charts-lib, `registerChartJs()`) is first loaded, i.e. long after this
 * eagerly imported domain module has been evaluated. chart.js merges the
 * tooltip plugin defaults into the resolved options at render time anyway, so
 * behaviour is unchanged.
 *
 * The type is `Partial<…>` for the same reason: the spread only ever satisfied
 * the full `TooltipOptions` *type*, never produced those 32 properties at
 * runtime. Every consumer spreads this object into its own tooltip options (or
 * assigns it to chart.js' deep-partial `options.plugins.tooltip`), so the
 * narrower type costs them nothing.
 */
export const BD_CHART_TOOLTIP_DEFAULT: Partial<TooltipOptions<ChartType>> = {
  backgroundColor: 'rgb(255,255,255)',
  titleFont: {
    family: 'Montserrat, sans-serif',
    size: 12,
    style: CHART_JS_DEFAULT_FONT_STYLE,
    lineHeight: CHART_JS_DEFAULT_FONT_LINE_HEIGHT,
    weight: CHART_JS_DEFAULT_FONT_WEIGHT,
  },
  bodyFont: {
    family: 'Montserrat, sans-serif',
    size: 14,
    style: CHART_JS_DEFAULT_FONT_STYLE,
    lineHeight: CHART_JS_DEFAULT_FONT_LINE_HEIGHT,
    weight: CHART_JS_DEFAULT_FONT_WEIGHT,
  },
  titleColor: 'rgba(0,0,0,0.84)',
  bodyColor: 'rgba(0,0,0,0.84)',
  titleSpacing: 8,
  padding: 12,
  cornerRadius: 2,
  borderWidth: 1,
  borderColor: 'rgba(0,0,1,0.15)',
  titleMarginBottom: 6,
};

/**
 * Copy of {@link BD_CHART_TOOLTIP_DEFAULT} with the given callbacks (falls back
 * to the default callbacks). Returns a fresh object per call so consumers can
 * mutate their options without touching the shared default.
 */
export function BD_CHART_TOOLTIP_DEFAULT_FACTORY<TType extends ChartType>(
  callbacks?: TooltipCallbacks<ChartType>,
): TooltipOptions<TType> {
  const options: TooltipOptions<TType> = {
    ...BD_CHART_TOOLTIP_DEFAULT,
  } as TooltipOptions<TType>;

  options.callbacks =
    (callbacks as TooltipCallbacks<TType>) || options.callbacks;

  return options;
}
